import {
  ClientMsgSchema,
  ServerMsgSchema,
  PROTOCOL_VERSION,
  type ClientMsg,
  type ServerMsg,
  type Intent,
  type RuleSet,
  type PlayerCount,
  type Stamp,
} from '@mj/protocol';

export type WsStatus = 'connecting' | 'open' | 'closed';

export interface WsClientOpts {
  url: string;
  seatTicket: string;
  /** DI for tests: defaults to globalThis.WebSocket. */
  WebSocketCtor?: typeof WebSocket;
  onMessage: (msg: ServerMsg) => void;
  onStatus?: (s: WsStatus) => void;
  /** reconnect backoff base ms (default 500), capped (default 8000). */
  backoffMs?: number;
  /** Optional reconnect ticket provider: called before each RE-connect (never the first connect) to mint a
   *  fresh seatTicket via idempotent /api/rooms/join. Returns the new ticket. If it throws, the client keeps
   *  retrying with backoff (NOT fatal). Without it, reconnect re-sends the seed ticket (will fail TICKET_REPLAY). */
  rejoin?: () => Promise<string>;
}

export class WsClient {
  private readonly opts: WsClientOpts;
  private readonly WebSocketCtor: typeof WebSocket;
  private readonly backoffMs: number;
  private readonly backoffCapMs: number;
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;
  private reconnectAttempt = 0;
  private lastAckSeq = 0;
  private nextClientSeq = 1;
  private currentStatus: WsStatus = 'closed';
  private currentTicket: string;
  private isFirstConnect = true;

  constructor(opts: WsClientOpts) {
    this.opts = opts;
    this.WebSocketCtor = opts.WebSocketCtor ?? globalThis.WebSocket;
    this.backoffMs = opts.backoffMs ?? 500;
    this.backoffCapMs = 8000;
    this.currentTicket = opts.seatTicket;
    this.connect();
  }

  get status(): WsStatus {
    return this.currentStatus;
  }

  /** highest wire seq durably applied (set via noteAck); sent on reconnect as resume.lastAckSeq. */
  noteAck(seq: number): void {
    this.lastAckSeq = Math.max(this.lastAckSeq, seq);
  }

  send(msg: ClientMsg): void {
    if (!this.isOpen()) return;
    this.socket!.send(JSON.stringify(ClientMsgSchema.parse(msg)));
  }

  sendIntent(intent: Intent): void {
    this.send({ t: 'intent', clientSeq: this.nextClientSeq++, intent });
  }

  ready(): void {
    this.send({ t: 'ready' });
  }

  /** Host-only (seat 0): leave the waiting lobby and start the match. */
  sendStart(botFill: boolean): void {
    this.send({ t: 'start', botFill });
  }

  /** Host-only, WAITING only: mutate the room's player count and/or ruleset before the match starts.
   *  The server rejects from a non-host (NOT_HOST) / once started (ROOM_STARTED) and re-broadcasts roomState. */
  sendUpdateConfig(players?: PlayerCount, ruleSet?: RuleSet): void {
    this.send({
      t: 'updateConfig',
      ...(players !== undefined ? { players } : {}),
      ...(ruleSet !== undefined ? { ruleSet } : {}),
    });
  }

  /** Graceful per-seat leave (frees this seat, never disbands the room). */
  sendLeave(): void {
    this.send({ t: 'leave' });
  }

  /** Send a preset stamp (server throttles to STAMP_THROTTLE_MS; broadcast to all seats). */
  sendStamp(stamp: Stamp): void {
    this.send({ t: 'stamp', stamp });
  }

  ping(): void {
    this.send({ t: 'ping', ts: Date.now() });
  }

  close(): void {
    this.intentionalClose = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.socket;
    this.socket = null;
    if (socket !== null) socket.close();
    this.setStatus('closed');
  }

  private connect(): void {
    if (this.intentionalClose) return;
    this.setStatus('connecting');
    if (this.isFirstConnect || !this.opts.rejoin) {
      this.isFirstConnect = false;
      this.openSocket();
      return;
    }
    // RECONNECT: mint a fresh ticket (idempotent join) before hello, else the burned jti → TICKET_REPLAY.
    void this.opts.rejoin().then(
      (ticket) => {
        if (this.intentionalClose) return;
        this.currentTicket = ticket;
        this.openSocket();
      },
      () => {
        // rejoin failed (room transiently unreachable / auth refresh in flight): back off and retry.
        if (this.intentionalClose) return;
        this.setStatus('closed');
        this.scheduleReconnect();
      },
    );
  }

  private openSocket(): void {
    const socket = new this.WebSocketCtor(this.opts.url);
    this.socket = socket;

    socket.addEventListener('open', () => {
      if (this.intentionalClose || this.socket !== socket) return;
      this.reconnectAttempt = 0;
      socket.send(JSON.stringify(ClientMsgSchema.parse({
        t: 'hello',
        protocolVersion: PROTOCOL_VERSION,
        seatTicket: this.currentTicket,
        ...(this.lastAckSeq > 0 ? { resume: { lastAckSeq: this.lastAckSeq } } : {}),
      })));
      this.setStatus('open');
    });

    socket.addEventListener('message', (ev: MessageEvent) => {
      const data = typeof ev.data === 'string' ? ev.data : String(ev.data);
      try {
        const parsed = ServerMsgSchema.safeParse(JSON.parse(data));
        if (parsed.success) this.opts.onMessage(parsed.data);
      } catch {
        // Ignore malformed frames; the next valid snapshot is authoritative.
      }
    });

    socket.addEventListener('close', () => this.handleDisconnect(socket));
    socket.addEventListener('error', () => this.handleDisconnect(socket));
  }

  private handleDisconnect(socket: WebSocket): void {
    if (this.intentionalClose || this.socket !== socket || this.currentStatus === 'closed') return;
    this.setStatus('closed');
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.intentionalClose || this.reconnectTimer !== null) return;
    const delay = Math.min(this.backoffMs * 2 ** this.reconnectAttempt, this.backoffCapMs);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private isOpen(): boolean {
    return this.socket !== null && this.socket.readyState === this.WebSocketCtor.OPEN;
  }

  private setStatus(s: WsStatus): void {
    if (this.currentStatus === s) return;
    this.currentStatus = s;
    this.opts.onStatus?.(s);
  }
}
